import { reactive } from 'vue'

interface ClientBrandingResponse {
  is_default: boolean
  display_name: string | null
  logo_light_url: string | null
  logo_dark_url: string | null
  favicon_url: string | null
  document_title: string | null
}

interface ClientBrandingState {
  isResolved: boolean
  isLoading: boolean
  isDefault: boolean
  notFound: boolean
  displayName: string | null
  logoLightUrl: string | null
  logoDarkUrl: string | null
  faviconUrl: string | null
  documentTitle: string | null
}

const DEFAULT_DOCUMENT_TITLE = 'DIGITRACK 2.0'
const DEFAULT_PANEL_HOSTNAMES = new Set(['localhost', '127.0.0.1', 'v2.digitrack.pl'])

export const clientBrandingState = reactive<ClientBrandingState>({
  isResolved: false,
  isLoading: false,
  isDefault: true,
  notFound: false,
  displayName: null,
  logoLightUrl: null,
  logoDarkUrl: null,
  faviconUrl: null,
  documentTitle: null
})

function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

function getCurrentPanelHost(): string {
  return isBrowser() ? window.location.host : ''
}

function getCurrentPanelHostname(): string {
  return isBrowser() ? window.location.hostname : ''
}

function isDefaultPanelHost(): boolean {
  return DEFAULT_PANEL_HOSTNAMES.has(getCurrentPanelHostname())
}

function setDefaultBrandingState(): void {
  clientBrandingState.isResolved = true
  clientBrandingState.isLoading = false
  clientBrandingState.isDefault = true
  clientBrandingState.notFound = false
  clientBrandingState.displayName = null
  clientBrandingState.logoLightUrl = null
  clientBrandingState.logoDarkUrl = null
  clientBrandingState.faviconUrl = null
  clientBrandingState.documentTitle = null
  updateDocumentTitle(DEFAULT_DOCUMENT_TITLE)
}

function setCustomBrandingState(response: ClientBrandingResponse): void {
  clientBrandingState.isResolved = true
  clientBrandingState.isLoading = false
  clientBrandingState.isDefault = false
  clientBrandingState.notFound = false
  clientBrandingState.displayName = response.display_name
  clientBrandingState.logoLightUrl = response.logo_light_url
  clientBrandingState.logoDarkUrl = response.logo_dark_url
  clientBrandingState.faviconUrl = response.favicon_url
  clientBrandingState.documentTitle = response.document_title
  updateDocumentTitle(response.document_title ?? DEFAULT_DOCUMENT_TITLE)
}

function setNotFoundState(): void {
  clientBrandingState.isResolved = true
  clientBrandingState.isLoading = false
  clientBrandingState.isDefault = false
  clientBrandingState.notFound = true
  clientBrandingState.displayName = null
  clientBrandingState.logoLightUrl = null
  clientBrandingState.logoDarkUrl = null
  clientBrandingState.faviconUrl = null
  clientBrandingState.documentTitle = null
  updateDocumentTitle(DEFAULT_DOCUMENT_TITLE)
}

function updateDocumentTitle(title: string): void {
  if (!isBrowser()) {
    return
  }

  document.title = title
}

function updateFavicon(href: string | null): void {
  if (!isBrowser() || !href) {
    return
  }

  const head = document.head
  if (!head) {
    return
  }

  let favicon = head.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (!favicon) {
    favicon = document.createElement('link')
    favicon.rel = 'icon'
    head.appendChild(favicon)
  }

  favicon.href = href
}

export async function resolveClientBranding(): Promise<void> {
  if (!isBrowser() || clientBrandingState.isResolved || clientBrandingState.isLoading) {
    return
  }

  if (isDefaultPanelHost()) {
    setDefaultBrandingState()
    return
  }

  clientBrandingState.isLoading = true

  try {
    const response = await fetch(
      `${process.env.VUE_APP_API_BASE_URL}/api/v1/public/client-branding/resolve?panel_host=${encodeURIComponent(getCurrentPanelHost())}`,
      {
        method: 'GET',
      }
    )

    if (response.status === 404) {
      setNotFoundState()
      return
    }

    if (!response.ok) {
      throw new Error(`Branding resolve failed with status ${response.status}`)
    }

    const branding = await response.json() as ClientBrandingResponse

    if (branding.is_default) {
      setDefaultBrandingState()
      return
    }

    setCustomBrandingState(branding)
    updateFavicon(branding.favicon_url)
  } catch (error) {
    console.error('Unable to resolve client branding', error)
    setDefaultBrandingState()
  }
}

export function getClientBrandingLogo(isDarkMode: boolean): string | null {
  if (!clientBrandingState.isResolved || clientBrandingState.isDefault) {
    return null
  }

  if (isDarkMode && clientBrandingState.logoDarkUrl) {
    return clientBrandingState.logoDarkUrl
  }

  return clientBrandingState.logoLightUrl
}

export function getClientBrandingName(): string | null {
  if (!clientBrandingState.isResolved || clientBrandingState.isDefault) {
    return null
  }

  return clientBrandingState.displayName
}

export function getClientBrandingDocumentTitle(pageTitle?: string): string {
  const baseTitle = !clientBrandingState.isDefault && clientBrandingState.documentTitle
    ? clientBrandingState.documentTitle
    : DEFAULT_DOCUMENT_TITLE

  return pageTitle ? `${baseTitle} | ${pageTitle}` : baseTitle
}
